import crypto from "crypto";
import dayjs from "dayjs";
import { MenuDataType, MenuItem, ShowMenuType } from "~/components/shared-components/mobile-menu/type";
import { LANG, OG_URL } from "./constant";
import { isBrowser } from "./envirement";

export function computeEtagFromData(data: any) {
  const json = JSON.stringify(data);
  return crypto.createHash("md5").update(json).digest("hex");
}

// Function to get dynamic URL
export const getDynamicURL = (): string => {
  if (typeof window !== "undefined") {
    return `${window.location.protocol}//${window.location.host}`;
  }

  return OG_URL;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const downloadFile = async (data: string, fileName: string) => {
  const pdfUrl = data;

  try {
    const response = await fetch(pdfUrl, {
      method: "GET",
      mode: "cors",
    });

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Error downloading file:", err);
  }
};

const getObjectKeys = (obj: any) => {
  if (!obj) return [];
  return Object.keys(obj);
};

const getLength = (obj: any) => {
  if (!obj) return 0;

  if (Array.isArray(obj)) {
    return obj.length;
  }

  if (typeof obj === "object" && obj !== null) {
    return getObjectKeys(obj).length;
  }

  if (typeof obj === "string") {
    return obj.length;
  }

  return 0;
};

const formatName = (name: string) => {
  if (name.includes(" ") || name.includes("-")) return name;
  if (name.length > 10) {
    return name.slice(0, 6) + "-\n" + name.slice(6);
  }
  return name;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatDateBaseOnDDMonthNameYY = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);

  const options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short", year: "numeric" };
  const formatted = new Intl.DateTimeFormat("en-GB", options).format(date).replace(" ", " ");
  return formatted;
};

// format the string to be used as a slug
const addDashInString = (slug: string) => {
  const words = slug.split(" ");
  const formattedSlug = words.join("-").toLocaleLowerCase();
  return formattedSlug;
};

// remove the dash from the string
const removeDashInString = (slug: string) => {
  if (!slug) return "";
  if (!slug.includes("-")) return slug;
  const words = slug.split("-");
  const formattedSlug = words.join(" ").toLocaleLowerCase();
  return formattedSlug;
};

// capitalize the first letter of the string
const capitalize = (slug: string) => {
  return slug?.charAt(0)?.toLocaleUpperCase() + slug?.slice(1);
};

function convertSecondsToHMS(duration_sec: number) {
  const hours = Math.floor(duration_sec / 3600);
  const minutes = Math.floor((duration_sec % 3600) / 60);
  const seconds = duration_sec % 60;

  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(seconds).padStart(2, "0");

  if (hours > 0) {
    const formattedHours = String(hours).padStart(2, "0");
    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  }

  return `${formattedMinutes}:${formattedSeconds}`;
}

function timeAgo(dateString: string) {
  const now = new Date();
  const past = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  const seconds = 60;
  const minutes = 60 * seconds;
  const hours = 24 * minutes;
  const days = 30 * hours;
  const months = 12 * days;

  if (diffInSeconds < seconds) {
    return "just now";
  } else if (diffInSeconds < minutes) {
    return `${Math.floor(diffInSeconds / seconds)} seconds ago`;
  } else if (diffInSeconds < hours) {
    return `${Math.floor(diffInSeconds / minutes)} minutes ago`;
  } else if (diffInSeconds < days) {
    return `${Math.floor(diffInSeconds / hours)} hours ago`;
  } else if (diffInSeconds < months) {
    return `${Math.floor(diffInSeconds / days)} days ago`;
  } else if (diffInSeconds < 12 * months) {
    return `${Math.floor(diffInSeconds / months)} months ago`;
  } else {
    return `${Math.floor(diffInSeconds / (12 * months))} years ago`;
  }
}

const truncateString = (str: string, length: number) => {
  if (str.length <= length) return str;
  return str.slice(0, length) + "...";
};

const removeUnderscore = (str: string) => {
  if (!str) return "";
  return str.replace(/_/g, " ").trim();
};

const wantsMobile = (v: ShowMenuType | null) => v === "web" || v === "both"; // this condition replace mobile to web because of client requirement change. only show web and both menu item display
const wantsDesktop = (v: ShowMenuType | null) => v === "web" || v === "both";

const categoryHasMobileFlag = (cat: MenuDataType): boolean => {
  if (wantsMobile(cat.show_menu ?? null)) return true;
  if (cat.menu_group?.some((g) => wantsMobile(g.show_menu ?? null))) return true;
  if (cat.menu_group?.some((g) => g.menu_item?.some((mi) => wantsMobile(mi.show_menu ?? null)))) return true;
  return false;
};

const getMobileItemsFromCategory = (cat: MenuDataType): MenuItem[] => {
  return (
    cat.menu_group?.flatMap((g) => {
      if (wantsMobile(g.show_menu)) return g.menu_item ?? [];
      return g.menu_item?.filter((mi) => wantsMobile(mi.show_menu)) ?? [];
    }) ?? []
  );
};

function getTimeLeftFromDateTime(date: string, timeStr: string) {
  const [hour, minute] = timeStr.split(":").map(Number);
  const target = dayjs(date)?.hour(hour)?.minute(minute)?.second(0);
  const now = dayjs();

  // Calculate diff
  const diff = target.diff(now);
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, totalSeconds: 0 };

  const dur = dayjs.duration(diff);
  return {
    days: Math.floor(dur.asDays()),
    hours: dur.hours(),
    minutes: dur.minutes(),
    seconds: dur.seconds(),
    totalSeconds: Math.floor(diff / 1000),
  };
}

const isMediaAudioOnly = (mediaType?: string | undefined) => {
  if (!mediaType) return undefined;
  return mediaType === "audio_only" ? "audio" : undefined;
};

const mediaAudioOnly = (mediaType?: string | undefined) => {
  if (!mediaType) return false;
  return mediaType === "audio_only";
};

// Detect iOS
const isIOS = () => {
  if (typeof window === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

const homeRouteString = (lang: string): { home: string; path: string } => {
  const obj = {
    home: lang === LANG.english ? "home" : "હોમ",
    path: "/",
  };
  return obj;
};

const stripHTML = (htmlString: string): string => {
  if (!isBrowser()) return htmlString;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  return doc.body.innerText;
};

export {
  addDashInString,
  capitalize,
  categoryHasMobileFlag,
  convertSecondsToHMS,
  downloadFile,
  formatDate,
  formatDateBaseOnDDMonthNameYY,
  formatName,
  getLength,
  getMobileItemsFromCategory,
  getObjectKeys,
  getTimeLeftFromDateTime,
  homeRouteString,
  isIOS,
  isMediaAudioOnly,
  mediaAudioOnly,
  removeDashInString,
  removeUnderscore,
  stripHTML,
  timeAgo,
  truncateString,
  wantsDesktop,
  wantsMobile,
};
